import * as amplitude from "@amplitude/analytics-browser";
import { EventProperties, UserProperties } from "./eventTypes";

// Constants
const AMPLITUDE_API_KEY = "953f898a26afb69a3c814c2055e7cbe6";
const APP_NAME = "ServiceQueue";

/**
 * Initializes Amplitude with default configuration.
 */
export const initAmplitude = () => {
  amplitude.init(AMPLITUDE_API_KEY, undefined, {
    defaultTracking: {
      sessions: true,
      pageViews: true,
    },
  });
};

/**
 * Creates standardized event properties with the app name included.
 *
 * @param baseProperties - The base event properties
 * @returns EventProperties with app name included
 */
const createEventProperties = (
  baseProperties: Omit<EventProperties, "app">
): EventProperties => {
  return {
    ...baseProperties,
    app: APP_NAME,
  };
};

/**
 * Tracks an event in Amplitude with error safety and standardized properties.
 *
 * @param userAndCompany - The user identifier (format: user@company)
 * @param userProperties - User-specific properties (company, user)
 * @param eventType - The name of the event (e.g., "UserInterfaceFormLoad")
 * @param eventProperties - Event properties (elapsed time, title, component name, version)
 */
export const logAmplitudeEventWithProps = (
  userAndCompany: string,
  userProperties: UserProperties,
  eventType: string,
  eventProperties: Omit<EventProperties, "app">
) => {
  try {
    // Set user ID
    amplitude.setUserId(userAndCompany);

    // Set user properties
    const identify = new amplitude.Identify();
    Object.entries(userProperties).forEach(([key, value]) => {
      identify.set(key, value);
    });
    amplitude.identify(identify);

    // Create standardized event properties with app name
    const completeEventProperties = createEventProperties(eventProperties);

    // Track the event
    amplitude.track(eventType, completeEventProperties);
  } catch (error) {
    console.error("Amplitude tracking error:", error);
  }
};

/**
 * Helper function to create standardized event properties with common defaults.
 *
 * @param title - The event title
 * @param componentName - The component name
 * @param version - The app version
 * @param elapsed_ms - The elapsed time (defaults to "124")
 * @returns EventProperties object with app name included
 */
export const createAmplitudeEventProperties = (
  title: string,
  componentName: string,
  version: string,
  elapsed_ms: string = "124"
): Omit<EventProperties, "app"> => {
  return {
    elapsed_ms,
    title,
    componentName,
    version,
  };
};

export const setAmplitudeUser = (userId: string, companyId: string) => {
  amplitude.setUserId(userId);
  amplitude.setGroup("company", companyId);
};

export const logAmplitudeEventWithPayload = (
  eventName: string,
  payload?: object
) => {
  amplitude.track(eventName, payload);
};
export const logAmplitudeEvent = (eventName: string, eventHandler: string) => {
  amplitude.track(eventName, {
    buttonName: eventHandler,
  });
};
export const logAmplitudeException = (
  error: unknown,
  context: Record<string, any> = {}
) => {
  const payload = {
    message: error instanceof Error ? error.message : JSON.stringify(error),
    stack: error instanceof Error ? error.stack : undefined,
    ...context,
  };
  //logAmplitudeEventWithPayload("Exception", payload);
};

export default amplitude;
